/**
 * GuestWalkthroughOverlay
 * Renders as a transparent React Native Modal so it always paints above the entire
 * native navigation chrome (tab bar, headers, FAB) regardless of parent z-index.
 *
 * Spotlight technique: a View with a very large borderWidth (3000) whose borderColor
 * is the dim colour. The inner "hole" sits over the target element. The border View
 * has pointerEvents="none" so all touches pass through to the underlying UI.
 */

import { useMemo, useState, useEffect, useRef } from 'react'
import {
  View,
  Text,
  Modal,
  Pressable,
  Dimensions,
  Animated,
  Platform,
  type ViewStyle,
} from 'react-native'
import { Image } from 'expo-image'
import LottieView from 'lottie-react-native'
import { useGuestWalkthroughStore } from '../../src/shared/stores/guestWalkthroughStore'
import { useSupabaseAuth } from '../../src/shared/contexts/SupabaseAuthContext'
import { useAnalytics } from '../../src/analytics/useAnalytics'
import { ANALYTICS_EVENTS } from '../../src/analytics'
import {
  WALKTHROUGH_CONFIG,
  WALKTHROUGH_STEPS,
  WALKTHROUGH_STEP_ORDER,
  SPOTLIGHT_BORDER_WIDTH,
  SPOTLIGHT_BORDER_RADIUS,
  NO_GAMES_FALLBACK_TOOLTIP,
} from '../../src/create/constants/guestWalkthrough.constants'
import {
  getWalkthroughOverlayStyles,
  getSpotlightBorderPositionStyle,
  getHandTapPositionStyle,
  getTooltipPositionStyle,
} from './GuestWalkthroughOverlay.styles'
import { SpotlightProxy } from './SpotlightProxy'
import { useTheme } from '../../src/theme/useTheme'
import { getAssetUrl, ASSET_PATHS, HAND_TAP_LOTTIE_SOURCE } from '../../constants/assets'
import { WEB_IMAGE_ALT_DECORATIVE } from '../../src/shared/helpers/imageWebAlt.helper'
import { markCreateWalkthroughSeen } from '../../src/tutorial/helpers/walkthroughSeenStorage.helper'

const MONKEY_IDLE_URI = getAssetUrl(ASSET_PATHS.AVATARS.monkey.idle)
const MONKEY_HAPPY_URI = getAssetUrl(ASSET_PATHS.AVATARS.monkey.happy)

/** When on the games_list step with no targetRect, show a fallback button after this delay */
const GAMES_LIST_FALLBACK_DELAY_MS = 2000

const HAND_TAP_HALF_SIZE = 48
const HAND_TAP_OFFSET_X = 4
/** Push hand-tap lower so the finger sits on the actionable control across steps. */
const HAND_TAP_OFFSET_Y = 24
/** Extra downward nudges for spotlighted steps (all platforms). */
const STEP_DOWN_SHIFT_BY_STEP: Partial<Record<string, number>> = {
  [WALKTHROUGH_STEPS.CREATE_BTN]: 26,
  // market_tab (the 1X2 chip) and match_details (the Home tile) spotlight small inline
  // controls measured live, so no nudge — a shift would push the hole/finger off them.
  [WALKTHROUGH_STEPS.MATCH_DETAILS_NEXT]: 26,
  [WALKTHROUGH_STEPS.PREDICTING_REVIEW]: 26,
  [WALKTHROUGH_STEPS.PARTICIPANTS_PICK]: 26,
  [WALKTHROUGH_STEPS.PARTICIPANTS_SEND]: 26,
}

/**
 * Steps whose spotlight target mounts asynchronously (the expanded card must render
 * its market picker first). If no target appears in time we surface a manual "Continue"
 * button that advances the tour, so a missing/empty markets payload can't hard-stick.
 */
const ASYNC_TARGET_STEPS: ReadonlySet<string> = new Set([
  WALKTHROUGH_STEPS.MARKET_TAB,
  WALKTHROUGH_STEPS.MATCH_DETAILS,
])

const TOOLTIP_MAX_WIDTH = 340
const TOOLTIP_MIN_MARGIN = 20

export function GuestWalkthroughOverlay() {
  const theme = useTheme()
  const styles = useMemo(() => getWalkthroughOverlayStyles(theme), [theme])

  const isActive = useGuestWalkthroughStore((s) => s.isActive)
  const currentStep = useGuestWalkthroughStore((s) => s.currentStep)
  const targetRect = useGuestWalkthroughStore((s) => s.targetRect)
  const overlayProxyAction = useGuestWalkthroughStore((s) => s.overlayProxyAction)
  const advanceStep = useGuestWalkthroughStore((s) => s.advanceStep)
  const dismissWalkthrough = useGuestWalkthroughStore((s) => s.dismissWalkthrough)
  const { track } = useAnalytics()
  const { userId } = useSupabaseAuth()

  const { width: screenWidth, height: screenHeight } = Dimensions.get('window')

  const tooltipCardHorizontalStyle = useMemo<ViewStyle>(() => {
    const available = screenWidth - 2 * TOOLTIP_MIN_MARGIN
    if (available <= TOOLTIP_MAX_WIDTH) {
      return { left: TOOLTIP_MIN_MARGIN, right: TOOLTIP_MIN_MARGIN }
    }
    const offset = Math.round((screenWidth - TOOLTIP_MAX_WIDTH) / 2)
    return { left: offset, right: offset }
  }, [screenWidth])

  // Card entrance animation — spring-slides up + fades in on every step change
  const cardOpacity = useRef(new Animated.Value(0)).current
  const cardTranslateY = useRef(new Animated.Value(24)).current

  useEffect(() => {
    if (!isActive || currentStep === null) return
    cardOpacity.setValue(0)
    cardTranslateY.setValue(24)
    const anim = Animated.parallel([
      Animated.timing(cardOpacity, {
        toValue: 1,
        duration: 280,
        useNativeDriver: true,
      }),
      Animated.spring(cardTranslateY, {
        toValue: 0,
        friction: 7,
        tension: 80,
        useNativeDriver: true,
      }),
    ])
    anim.start()
    return () => anim.stop()
  }, [currentStep, isActive, cardOpacity, cardTranslateY])

  // Mark walkthrough seen whenever it deactivates (dismiss or last-step completion)
  const wasActiveRef = useRef(false)
  useEffect(() => {
    if (isActive) {
      wasActiveRef.current = true
    } else if (wasActiveRef.current) {
      wasActiveRef.current = false
      if (userId) void markCreateWalkthroughSeen(userId)
    }
  }, [isActive, userId])

  // Fallback: on games_list (no games today) or the async expanded-card steps (markets
  // failed to load), if no targetRect appears within the delay, show a manual button so
  // the user isn't stuck. games_list ends the tour; the async steps advance it.
  const stepCanStall =
    currentStep !== null &&
    (currentStep === WALKTHROUGH_STEPS.GAMES_LIST || ASYNC_TARGET_STEPS.has(currentStep))
  const [showFallbackBtn, setShowFallbackBtn] = useState(false)
  useEffect(() => {
    if (!stepCanStall || targetRect !== null) {
      setShowFallbackBtn(false)
      return
    }
    const timer = setTimeout(() => setShowFallbackBtn(true), GAMES_LIST_FALLBACK_DELAY_MS)
    return () => clearTimeout(timer)
  }, [stepCanStall, targetRect])

  useEffect(() => {
    if (!isActive || currentStep === null) return
    track(ANALYTICS_EVENTS.GUEST_WALKTHROUGH_STEP_VIEWED, {
      walkthrough_step: currentStep,
    })
  }, [isActive, currentStep, track])

  if (!isActive || currentStep === null) return null

  const config = WALKTHROUGH_CONFIG[currentStep]
  const monkeyUri = config.monkeyMood === 'happy' ? MONKEY_HAPPY_URI : MONKEY_IDLE_URI
  // On games_list a fired fallback means no game could be spotlighted (no playable fixtures);
  // end the tour gracefully. On the async expanded-card steps it means the markets never
  // rendered; let the user advance past it instead of hard-sticking.
  const isNoGamesFallback = showFallbackBtn && currentStep === WALKTHROUGH_STEPS.GAMES_LIST
  const showNextBtn = config.showNextBtn || showFallbackBtn
  const nextBtnLabel = config.showNextBtn
    ? config.nextBtnLabel
    : isNoGamesFallback ? 'Got it!' : 'Continue'
  const tooltipText = isNoGamesFallback ? NO_GAMES_FALLBACK_TOOLTIP : config.tooltip

  const stepIndex = WALKTHROUGH_STEP_ORDER.indexOf(currentStep)
  const totalSteps = WALKTHROUGH_STEP_ORDER.length

  const handleSkip = () => {
    dismissWalkthrough()
    track(ANALYTICS_EVENTS.GUEST_WALKTHROUGH_DISMISSED, { walkthrough_step: currentStep })
  }

  // Normal CTA advances; the no-games fallback ends the tour (dismiss marks it seen).
  // Track a distinct reason so this auto-end isn't read as a user rage-quit.
  const handleNext = () => {
    if (isNoGamesFallback) {
      dismissWalkthrough()
      track(ANALYTICS_EVENTS.GUEST_WALKTHROUGH_DISMISSED, {
        walkthrough_step: currentStep,
        reason: 'no_games',
      })
      return
    }
    advanceStep()
  }

  const tooltipPosition = getTooltipPositionStyle(targetRect, screenHeight)

  const stepDownShift = Platform.OS === 'web' ? 0 : (STEP_DOWN_SHIFT_BY_STEP[currentStep] ?? 0)
  const adjustedTargetRect =
    targetRect !== null && stepDownShift > 0
      ? { ...targetRect, y: targetRect.y + stepDownShift }
      : targetRect

  return (
    <Modal
      transparent
      visible={isActive}
      animationType="fade"
      statusBarTranslucent
      onRequestClose={handleSkip}
    >
      <View style={styles.overlay} pointerEvents="box-none">

        {/* ── Spotlight / dim ── */}
        {adjustedTargetRect !== null ? (
          <View
            style={[
              styles.spotlightBorder,
              getSpotlightBorderPositionStyle(
                adjustedTargetRect,
                SPOTLIGHT_BORDER_WIDTH,
                SPOTLIGHT_BORDER_RADIUS,
              ),
            ]}
            pointerEvents="none"
          />
        ) : (
          <View style={styles.fullDim} pointerEvents="none" />
        )}

        {/* ── Tooltip card ── */}
        <Animated.View
          style={[
            styles.tooltipCard,
            tooltipCardHorizontalStyle,
            tooltipPosition,
            { opacity: cardOpacity, transform: [{ translateY: cardTranslateY }] },
          ]}
          pointerEvents="auto"
        >
          {/* Monkey + text column */}
          <View style={styles.mainRow}>
            <Image
              source={{ uri: monkeyUri }}
              style={styles.monkeyImage}
              alt={WEB_IMAGE_ALT_DECORATIVE}
            />
            <View style={styles.textColumn}>
              <View style={styles.stepRow}>
                <Text style={styles.stepLabel}>Step {stepIndex + 1} of {totalSteps}</Text>
                <Pressable style={styles.skipButton} onPress={handleSkip} accessibilityRole="button">
                  <Text style={styles.skipButtonText}>Skip</Text>
                </Pressable>
              </View>
              <Text style={styles.tooltipText}>{tooltipText}</Text>
            </View>
          </View>

          {/* Progress dots */}
          <View style={styles.progressRow}>
            {WALKTHROUGH_STEP_ORDER.map((step, dotIndex) => (
              <View
                key={step}
                style={[styles.progressDot, stepIndex === dotIndex && styles.progressDotActive]}
              />
            ))}
          </View>

          {/* Primary CTA — only shown when configured or fallback triggered */}
          {showNextBtn && (
            <Pressable style={styles.nextButton} onPress={handleNext} accessibilityRole="button">
              <Text style={styles.nextButtonText}>{nextBtnLabel}</Text>
            </Pressable>
          )}
        </Animated.View>

        {(currentStep === WALKTHROUGH_STEPS.CREATE_BTN ||
          currentStep === WALKTHROUGH_STEPS.GAMES_LIST ||
          currentStep === WALKTHROUGH_STEPS.MARKET_TAB ||
          currentStep === WALKTHROUGH_STEPS.MATCH_DETAILS ||
          currentStep === WALKTHROUGH_STEPS.MATCH_DETAILS_NEXT ||
          currentStep === WALKTHROUGH_STEPS.PREDICTING_REVIEW ||
          currentStep === WALKTHROUGH_STEPS.PARTICIPANTS_PICK ||
          currentStep === WALKTHROUGH_STEPS.PARTICIPANTS_SEND) &&
          adjustedTargetRect !== null &&
          overlayProxyAction !== null && (
          <SpotlightProxy
            targetRect={adjustedTargetRect}
            proxyStyle={styles.spotlightProxy}
            onPress={overlayProxyAction}
            accessibilityLabel="Use highlighted walkthrough action"
          />
        )}

        {/* ── Hand_tap Lottie ── */}
        {config.showHandTap && adjustedTargetRect !== null && (
          <View
            style={[
              styles.handTapContainer,
              getHandTapPositionStyle(
                adjustedTargetRect,
                HAND_TAP_HALF_SIZE,
                HAND_TAP_OFFSET_X,
                HAND_TAP_OFFSET_Y,
              ),
            ]}
            pointerEvents="none"
          >
            <LottieView
              source={HAND_TAP_LOTTIE_SOURCE}
              style={styles.handTapLottie}
              loop
              autoPlay
            />
          </View>
        )}

      </View>
    </Modal>
  )
}
